import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import verifyimg from '@/js/images/signup-select.webp';
import { setVerificationStatusAction } from '@/js/reduxstore/actions/authActions';
import makershakrslogo from '@/js/images/blackLogo.svg';
import { cn } from '@/lib/utils';
import { ChevronLeft, Loader2 } from 'lucide-react';

// Form validation
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import createApiClient from '@/api/axiosInstance';

const FormSchema = z.object({
  pin: z.string().min(6, {
    message: 'Your one-time password must be 6 characters.',
  }),
});

const Verification = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiClient = createApiClient(navigate, dispatch);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Get user data and token from Redux store
  const userData = useSelector((state) => state.authReducer.userData);
  const token = useSelector((state) => state.authReducer.token);
  const isVerified = useSelector((state) => state.authReducer.isVerified);

  const userEmail = userData?.email;

  const form = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      pin: '',
    },
  });

  // Redirect if already verified or no token
  useEffect(() => {
    try {
      if (isVerified && !userData?.role) {
        console.log('✅ User verified! Redirecting to role selection...');
        navigate('/roles');
        return;
      }
      if (!token) {
        console.log('⚠️ No auth token found! Redirecting to signup...');
        navigate('/signup');
        return;
      }
    } catch (error) {
      logError('checking verification status', error);
    }
  }, [isVerified, token, navigate, userData?.role]);

  // Timer for resend button
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setResendDisabled(false);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const startResendTimer = () => {
    setResendDisabled(true);
    setResendTimer(30); // 30 seconds cooldown
  };

  const handleSendAgain = async () => {
    // TODO: ADD TOAST
    if (resendDisabled) {
      console.log('⏳ Resend is currently disabled. Please wait...');
      return;
    }

    console.log('� Initiating OTP resend...');
    setIsLoading(true);
    setError('');

    try {
      const response = await apiClient.post('/otp/re-send', JSON.stringify({}));

      console.log('✅ OTP resent successfully!');
      startResendTimer();
    } catch (error) {
      logError('resending OTP', error, {
        // endpoint: 'https://backend.makersharks.ai/otp/re-send',
        endpoint: 'http://localhost:8000/otp/re-send',
        statusCode: error.response?.status,
      });
      setError('Failed to resend verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const logError = (context, error, details = {}) => {
    const timestamp = new Date().toLocaleTimeString();
    const friendlyMessage = `🔍 [${timestamp}] Oops! Something went wrong while ${context}`;

    console.group(friendlyMessage);
    console.log('📝 Error Message:', error.message || error);
    if (Object.keys(details).length > 0) {
      console.log('🔍 Additional Details:', details);
    }
    console.log('📍 Component State:', {
      isLoading,
      resendDisabled,
      resendTimer,
      userEmail,
      isVerified,
    });
    console.trace('🔍 Stack Trace:');
    console.groupEnd();
  };

  const handleChangeEmail = () => {
    navigate('/signup');
  };

  const onSubmit = async (data) => {
    console.log('🔐 Initiating OTP verification...');
    setIsLoading(true);
    setError('');

    try {
      const response = await apiClient.post(
        '/otp/verify',
        JSON.stringify({
          otp: data.pin,
        }),
      );

      console.log(
        '✅ OTP verified successfully! Redirecting to role selection...',
      );
      dispatch(setVerificationStatusAction(true));
      navigate('/roles');
    } catch (error) {
      const error_message = error?.response?.data?.error_message;
      console.log(error_message);

      switch (error_message) {
        case 'Invalid OTP entered':
          setError('Invalid verification code. Please try again.');
          break;

        default:
          setError('There is a Network Error. Please try again later.');
          break;
      }

      logError('verifying OTP', error, {
        // endpoint: 'https://backend.makersharks.ai/otp/verify',
        endpoint: 'http://localhost:8000/otp/verify',
        statusCode: error.response?.status,
        pinLength: data.pin.length,
      });
      form.reset();
    } finally {
      setIsLoading(false);
    }
  };

  // Common class for OTP input styles
  const otpInputClass = 'w-16 h-16 text-center text-2xl border-gray-400';

  return (
    <>
      <div className="splitinhalf flex justify-center min-h-[calc(100vh-70px)]">
        {/* ---- First Half ---- */}
        <div className="half-1 lg:flex w-1/2 items-center justify-center hidden">
          <div className="flex mr-28 flex-col items-end justify-end transform transition-transform duration-300 lg:scale-90 md:scale-75">
            {/* ----- Left Image ----- */}
            <img
              src={verifyimg}
              alt="verification"
              width={640}
              className="transform transition-transform duration-300"
            />
            {/* ----- Box UI ----- */}
            <div
              className="piece-box w-[350px] h-[124px] fixed rounded-3xl text-[15px] p-5 text-gray-600 text-center"
              style={{
                background:
                  'linear-gradient(97.27deg, rgba(118, 54, 217, 0.09) 5.17%, rgba(250, 250, 250, 0.6) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.11), rgba(0, 0, 0, 0.12))',
                // Scale down width and height while keeping the box in the same fixed position
                // transform: 'scale(0.9)', // Apply the scale directly to the box
                transformOrigin: 'top left', // Ensures the box scales down from the top right corner
              }}
            >
              Find the perfect manufacturer, get quotes, and manage purchase
              orders effortlessly. Bring your medical device to market faster!
            </div>
          </div>
        </div>

        {/* Second half - Verification Form */}
        <div className="relative w-full xl:w-1/2 flex bg-gray-100 items-center overflow-y-auto xl:justify-center justify-center">
          <div className="absolute top-4 left-4">
            <Button
              className="text-md font-roboto bg-gray-100 text-gray-500 border border-transparent px-3 py-2 hover:bg-gray-100 hover:border-veryLightPurple hover:text-lightPurple ease-in-out duration-200"
              onClick={() => navigate(-1)}
            >
              <ChevronLeft className="w-5 mr-2" />
              back
            </Button>
          </div>

          <div className="verification-box flex w-5/6 flex-col justify-center items-center text-center">
            {/* Logo section */}
            <div className="flex justify-center">
              <img
                src={makershakrslogo}
                className="w-[67px] hidden md:block"
                alt="MakerSharks-logo"
              />
              <h1 className="items-center hidden md:flex ml-3 font-roboto text-2xl lg:text-3xl font-semibold">
                MakerSharks
              </h1>
            </div>

            {/* Verification instructions */}
            <p className="mt-10 mb-5 font-bold font-roboto text-3xl lg:text-2xl text-gray-800 text-center">
              Verify your Email
            </p>

            <p className="mt-3 text-base lg:text-md text-gray-500 text-center">
              We have sent an
              <span className="font-semibold"> OTP </span>
              to <span className="font-semibold">{userEmail}</span>
            </p>

            {error && <p className="text-red-500 mt-2 text-sm">{error}</p>}

            {/* OTP Input Form */}
            <div className="flex items-center justify-center px-10 pb-10 pt-0 bg-gray-100">
              <div className="w-full max-w-xl px-6 pb-6 pt-0 rounded-lg">
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-8 p-4"
                  >
                    <FormField
                      control={form.control}
                      name="pin"
                      render={({ field }) => (
                        <FormItem className="space-y-6">
                          <FormLabel className="text-center text-gray-500 font-semibold block">
                            Enter the One-Time Password
                          </FormLabel>
                          <FormControl>
                            <InputOTP
                              maxLength={6}
                              value={field.value}
                              onChange={(value) => field.onChange(value)}
                              className="flex justify-center"
                            >
                              <InputOTPGroup className="flex justify-center">
                                <InputOTPSlot
                                  index={0}
                                  className={`${otpInputClass} focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple`}
                                />
                                <InputOTPSlot
                                  index={1}
                                  className={otpInputClass}
                                />
                                <InputOTPSlot
                                  index={2}
                                  className={otpInputClass}
                                />
                                <InputOTPSlot
                                  index={3}
                                  className={otpInputClass}
                                />
                                <InputOTPSlot
                                  index={4}
                                  className={otpInputClass}
                                />
                                <InputOTPSlot
                                  index={5}
                                  className={otpInputClass}
                                />
                              </InputOTPGroup>
                            </InputOTP>
                          </FormControl>
                          <FormMessage className="text-center" />
                        </FormItem>
                      )}
                    />
                    <div className="text-center text-gray-500 text-sm font-roboto">
                      <div className="">
                        Didn't receive the OTP?
                        {resendTimer > 0 ? (
                          <span className="ml-2 text-gray-400">
                            Resend available in {resendTimer}s
                          </span>
                        ) : (
                          <span
                            className={cn(
                              'ml-2 text-lightPurple cursor-pointer hover:underline hover:text-darkPurple ease-in-out duration-200',
                              {
                                'opacity-50 cursor-not-allowed':
                                  isLoading || resendDisabled,
                              },
                            )}
                            onClick={
                              !isLoading && !resendDisabled
                                ? handleSendAgain
                                : undefined
                            }
                          >
                            Send Again
                          </span>
                        )}
                      </div>
                      <div className="mt-1">
                        Change your email address
                        <span
                          className={cn(
                            'ml-2 text-lightPurple cursor-pointer hover:underline hover:text-darkPurple ease-in-out duration-200',
                          )}
                          onClick={handleChangeEmail}
                        >
                          here
                        </span>
                      </div>

                      <div className="flex justify-center">
                        <Button
                          type="submit"
                          disabled={isLoading}
                          className="w-full font-semibold text-md font-roboto bg-lightPurple mt-5 text-white px-4 py-2 rounded-md
                          hover:bg-darkerPurple hover:text-white ease-in-out duration-200 disabled:opacity-50"
                        >
                          {/* {isLoading ? 'Verifying...' : 'Verify'} */}

                          {isLoading ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            'Verify'
                          )}
                        </Button>
                      </div>
                    </div>
                  </form>
                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Verification;
